import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { parseAmazonOrderCsv } from "../utils/amazonOrderMatch";
import { RESPONSIBLE_PEOPLE } from "../utils/people";

function money(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "";
  return amount.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

function MatchTable({ title, rows, empty, columns }) {
  return (
    <div className="mt-4">
      <h4 className="text-sm font-semibold text-gray-800 mb-2">
        {title} ({rows.length})
      </h4>
      {rows.length === 0 ? (
        <p className="text-sm text-gray-500">{empty}</p>
      ) : (
        <div className="overflow-x-auto border border-gray-200 rounded-md">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-600">
              <tr>
                {columns.map((column) => (
                  <th key={column.key} className="px-3 py-2 font-medium">
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={`${row.orderId || "row"}-${index}`} className="border-t border-gray-100">
                  {columns.map((column) => (
                    <td key={column.key} className="px-3 py-2 align-top text-gray-800">
                      {column.render(row)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const orderColumns = [
  { key: "date", label: "Order date", render: (row) => row.orderPlaced },
  { key: "total", label: "Total", render: (row) => money(row.total) },
  { key: "item", label: "Items", render: (row) => row.itemTitle || "—" },
];

const chargeColumns = [
  ...orderColumns,
  { key: "chargeDate", label: "Charge date", render: (row) => row.chargeDate },
  { key: "description", label: "Card charge", render: (row) => row.description },
  {
    key: "days",
    label: "Days later",
    render: (row) => (row.dayDiff === 0 ? "Same day" : row.dayDiff),
  },
];

export default function AmazonOrderMatcher() {
  const queryClient = useQueryClient();
  const [person, setPerson] = useState("Barry");
  const [fileName, setFileName] = useState("");
  const [parsed, setParsed] = useState(null);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [appliedCount, setAppliedCount] = useState(null);

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    setPreview(null);
    setAppliedCount(null);
    setError("");
    setParsed(null);
    setFileName("");
    if (!file) return;

    try {
      const text = await file.text();
      const next = parseAmazonOrderCsv(text);
      setParsed(next);
      setFileName(file.name);
    } catch (err) {
      setError(err.message || "Could not read that spreadsheet");
    }
  };

  const previewMatches = async () => {
    if (!parsed) return;
    setBusy("preview");
    setError("");
    setAppliedCount(null);
    try {
      const result = await api.previewAmazonOrderMatch(person, parsed.orders);
      setPreview(result);
    } catch (err) {
      setError(err.message || "Could not match those orders");
    } finally {
      setBusy("");
    }
  };

  const applyMatches = async () => {
    if (!parsed || !preview?.matched?.length) return;
    setBusy("apply");
    setError("");
    try {
      const result = await api.applyAmazonOrderMatch(person, parsed.orders);
      setPreview(result);
      setAppliedCount(result.updated || 0);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["transactions"] }),
        queryClient.invalidateQueries({ queryKey: ["financial"] }),
        queryClient.invalidateQueries({ queryKey: ["categories", "spending"] }),
        queryClient.invalidateQueries({ queryKey: ["daily-spending"] }),
        queryClient.invalidateQueries({ queryKey: ["budget"] }),
      ]);
    } catch (err) {
      setError(err.message || "Could not assign those charges");
    } finally {
      setBusy("");
    }
  };

  return (
    <div>
      <h3 className="text-md font-semibold text-gray-700 mb-2">
        Amazon order history
      </h3>
      <p className="text-sm text-gray-600 mb-4">
        Upload the order spreadsheet from Amazon and choose who it belongs to.
        Card charges are matched when the description is Amazon, the amount
        matches to the cent, and the charge date is the order date or up to
        five days later. Cancelled and $0 orders are skipped. This does not
        create a keyword, and it will not change a charge already assigned to
        someone else.
      </p>

      <div className="grid gap-4 sm:grid-cols-2 mb-4">
        <label className="block text-sm font-medium text-gray-700">
          Spreadsheet
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={handleFile}
            className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100"
          />
        </label>
        <label className="block text-sm font-medium text-gray-700">
          This report is for
          <select
            value={person}
            onChange={(event) => {
              setPerson(event.target.value);
              setPreview(null);
              setAppliedCount(null);
            }}
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          >
            {RESPONSIBLE_PEOPLE.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </label>
      </div>

      {parsed && (
        <p className="text-sm text-gray-700 mb-3">
          {fileName}: {parsed.orders.length} chargeable orders
          {parsed.skipped.length > 0
            ? `, ${parsed.skipped.length} skipped (cancelled or $0)`
            : ""}
          .
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={previewMatches}
          disabled={!parsed || busy !== ""}
          className="px-4 py-2 rounded-md bg-purple-600 text-white text-sm font-semibold disabled:opacity-50"
        >
          {busy === "preview" ? "Matching…" : "Preview matches"}
        </button>
        <button
          type="button"
          onClick={applyMatches}
          disabled={!preview?.matched?.length || busy !== ""}
          className="px-4 py-2 rounded-md bg-gray-900 text-white text-sm font-semibold disabled:opacity-50"
        >
          {busy === "apply"
            ? "Assigning…"
            : `Assign ${preview?.matched?.length || 0} to ${person}`}
        </button>
      </div>

      {error && (
        <p className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          {error}
        </p>
      )}

      {appliedCount != null && (
        <p className="mt-3 text-sm text-green-800 bg-green-50 border border-green-200 rounded-md px-3 py-2">
          Assigned {appliedCount} charge{appliedCount === 1 ? "" : "s"} to {person}.
        </p>
      )}

      {preview && (
        <>
          <MatchTable
            title="Will assign"
            rows={preview.matched || []}
            empty="No unassigned Amazon charges matched these orders."
            columns={chargeColumns}
          />
          <MatchTable
            title={`Already ${preview.person || person}`}
            rows={preview.already || []}
            empty="None of the matches were already assigned to this person."
            columns={chargeColumns}
          />
          <MatchTable
            title="Left alone — already someone else"
            rows={preview.conflicts || []}
            empty="No matching charge was already assigned to a different person."
            columns={[
              ...chargeColumns,
              {
                key: "person",
                label: "Current person",
                render: (row) => row.currentPerson,
              },
            ]}
          />
          <MatchTable
            title="Orders with no card charge found"
            rows={preview.unmatched || []}
            empty="Every chargeable order matched a card charge."
            columns={orderColumns}
          />
          {parsed?.skipped?.length > 0 && (
            <MatchTable
              title="Skipped"
              rows={parsed.skipped}
              empty=""
              columns={[
                ...orderColumns,
                { key: "reason", label: "Reason", render: (row) => row.reason },
              ]}
            />
          )}
        </>
      )}
    </div>
  );
}
